import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Sliders,
  Calendar as CalendarIcon,
  Clock,
  ExternalLink,
  UserPlus,
  Bell,
  Lock,
  Settings as SettingsIcon,
  X,
  ArrowLeft,
} from 'lucide-react';
import { APPOINTMENT_STATUS_OPTIONS, calendarAccessSchema, calendarServiceTypeSchema, type CalendarAccess, type CalendarServiceType } from '@easyflow/shared';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionContext';
import SettingsSection from '@/components/calendar/SettingsSection';
import ServiceTypesSettings from '@/components/calendar/ServiceTypesSettings';
import AvailabilityModeSettings from '@/components/calendar/AvailabilityModeSettings';
import AppointmentFormsTab from '@/components/calendar/AppointmentFormsTab';
import ReassignmentPlansPanel from '@/components/calendar/ReassignmentPlansPanel';
import AppointmentAutomations from '@/components/calendar/AppointmentAutomations';
import SkillOverrideAuditPanel from '@/components/calendar/SkillOverrideAuditPanel';
import OverrideAuditPanel from '@/components/calendar/OverrideAuditPanel';
import CalendarNotificationsTab from '@/components/calendar/CalendarNotificationsTab';
import CalendarBookingWindowSettings from '@/components/calendar/CalendarBookingWindowSettings';
import { apiClient } from '@/lib/api';

const SECTION_BACK_COMPAT: Record<string, string> = {
  forms: 'forms',
  availability: 'availability',
  reassignments: 'reassignments',
  'skill-overrides': 'skill-overrides',
  'override-audit': 'skill-overrides',
  automations: 'automations',
  types: 'types',
  general: 'general',
  notifications: 'notifications',
};

type SectionId =
  | 'general'
  | 'types'
  | 'availability'
  | 'forms'
  | 'reassignments'
  | 'automations'
  | 'notifications'
  | 'skill-overrides';

interface SectionDef {
  id: SectionId;
  label: string;
  icon: React.ReactNode;
  description?: string;
  access?: 'manage' | 'broad';
}

const SECTIONS: SectionDef[] = [
  { id: 'general', label: 'Generali', icon: <Sliders className="w-4 h-4" />, description: 'Opzioni globali del calendario' },
  { id: 'types', label: 'Tipi Appuntamento', icon: <CalendarIcon className="w-4 h-4" />, description: 'Durata e servizi associati alle tipologie' },
  { id: 'availability', label: 'Disponibilità', icon: <Clock className="w-4 h-4" />, description: 'Modalità di assegnazione e parametri di disponibilità' },
  { id: 'forms', label: 'Form Pubblici', icon: <ExternalLink className="w-4 h-4" />, description: 'Form di prenotazione pubblici', access: 'broad' },
  { id: 'reassignments', label: 'Riassegnazioni', icon: <UserPlus className="w-4 h-4" />, description: 'Piani da verificare e applicare', access: 'broad' },
  { id: 'automations', label: 'Automazioni', icon: <Bell className="w-4 h-4" />, description: 'Promemoria e automazioni post-appuntamento', access: 'manage' },
  { id: 'notifications', label: 'Notifiche', icon: <Bell className="w-4 h-4" />, description: 'Canali di notifica' },
  { id: 'skill-overrides', label: 'Audit override', icon: <Lock className="w-4 h-4" />, description: 'Cronologia delle eccezioni alle regole', access: 'broad' },
];

interface CalendarSettingsPageProps {
  /**
   * 'page'     → renderizzata come pagina autonoma su /calendar/settings
   *              (header sticky con titolo + Chiudi, normalizza ?tab → ?section).
   * 'embedded' → renderizzata dentro un tab del Calendar Hub
   *              (no header, no Chiudi; legge solo ?section, ignora ?tab
   *              perché in embedded ?tab indica il top-level tab del calendario).
   */
  mode?: 'page' | 'embedded';
  access?: CalendarAccess | null;
}

export default function CalendarSettingsPage({ mode = 'page', access: suppliedAccess }: CalendarSettingsPageProps = {}) {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const embedded = mode === 'embedded';
  const [loadedAccess, setLoadedAccess] = useState<CalendarAccess | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  const access = suppliedAccess ?? loadedAccess;
  const canManage = hasPermission('calendar.manage') && access?.canManage === true;
  const canManageBroad = canManage && access?.isBroad === true;
  useEffect(() => {
    if (embedded) return;
    let cancelled = false;
    setLoadedAccess(null);
    setAccessError(null);
    const day = new Date().toISOString().slice(0, 10);
    void apiClient.get<{ access: unknown }>(`/calendar/bootstrap?date=${day}`).then(r => {
      if (!r.success || !r.data) throw new Error('Impossibile verificare i permessi Calendario');
      const parsed = calendarAccessSchema.parse(r.data.access);
      if (!cancelled) setLoadedAccess(parsed);
    }).catch(() => {
      if (!cancelled) setAccessError('Impossibile verificare i permessi Calendario. Ricarica la pagina.');
    });
    return () => { cancelled = true; };
  }, [embedded, tenantSlug, user?.id]);

  const visibleSections = useMemo(
    () => SECTIONS.filter(s => !s.access || (s.access === 'broad' ? canManageBroad : canManage)),
    [canManageBroad, canManage],
  );

  // Resolve a section id from the current URL query, with validation and
  // fallback. Used both for initial state and to keep `activeSection` in
  // sync if the URL changes externally (deep-link, browser navigation).
  const resolveSectionFromUrl = useCallback((): SectionId => {
    const sec = searchParams.get('section');
    if (sec && SECTIONS.some(s => s.id === sec)) return sec as SectionId;
    // In embedded ?tab indica il top-level tab del Calendar Hub, NON una
    // sezione settings → non leggerlo come back-compat per evitare di
    // confondere "tab=calendar" con una sezione inesistente.
    if (!embedded) {
      const tab = searchParams.get('tab');
      if (tab && SECTION_BACK_COMPAT[tab]) {
        const mapped = SECTION_BACK_COMPAT[tab];
        if (SECTIONS.some(s => s.id === mapped)) return mapped as SectionId;
      }
    }
    return 'general';
  }, [embedded, searchParams]);

  const [activeSection, setActiveSection] = useState<SectionId>(() => resolveSectionFromUrl());

  // Keep activeSection synced with the URL: if section/tab in the query
  // changes externally (deep link, back/forward), reflect it locally.
  // Also normalize legacy ?tab=... to ?section=... and fill in ?section
  // when missing, so the URL is always canonical.
  useEffect(() => {
    // La normalizzazione ?tab → ?section va fatta SOLO in modalità pagina:
    // in embedded il param ?tab è di proprietà del Calendar Hub.
    if (!embedded) {
      const tab = searchParams.get('tab');
      if (tab && SECTION_BACK_COMPAT[tab]) {
        const next = new URLSearchParams(searchParams);
        next.delete('tab');
        next.set('section', SECTION_BACK_COMPAT[tab]);
        setSearchParams(next, { replace: true });
        return;
      }
    }
    const sec = searchParams.get('section');
    const resolved = resolveSectionFromUrl();
    // Canonicalize ?section quando manca o è invisibile (es. non-admin che
    // cade su ?section=skill-overrides): riscriviamo alla sezione risolta
    // così URL e stato restano allineati. In embedded lo facciamo solo se
    // l'utente è effettivamente sul tab Settings, altrimenti scriveremmo
    // ?section a casaccio mentre l'utente è su Calendario/Planning/ecc.
    const onSettingsTab = !embedded || searchParams.get('tab') === 'settings';
    const isValidVisible = !!sec && SECTIONS.some(s => s.id === sec);
    if (!isValidVisible && onSettingsTab) {
      const next = new URLSearchParams(searchParams);
      next.set('section', resolved);
      setSearchParams(next, { replace: true });
    }
    if (resolved !== activeSection) {
      setActiveSection(resolved);
    }
  }, [searchParams, visibleSections, embedded, activeSection, resolveSectionFromUrl, setSearchParams]);

  const contentRef = useRef<HTMLDivElement | null>(null);

  const changeSection = (id: SectionId) => {
    setActiveSection(id);
    const next = new URLSearchParams(searchParams);
    next.set('section', id);
    setSearchParams(next, { replace: true });
    // Scroll content area to top on mobile
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    // Bring active tab into view in the mobile top-bar
    queueMicrotask(() => {
      const el = document.querySelector<HTMLButtonElement>(`[data-settings-tab="${id}"]`);
      el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    });
  };

  const handleClose = () => {
    const fromTab = searchParams.get('from');
    const target = `/${tenantSlug}/calendar${fromTab ? `?tab=${encodeURIComponent(fromTab)}` : ''}`;
    navigate(target);
  };

  // serviceTypes are needed by AppointmentAutomations; load once
  const [serviceTypes, setServiceTypes] = useState<CalendarServiceType[]>([]);
  const [typesError, setTypesError] = useState<string | null>(null);
  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<unknown>('/calendar/types');
        if (!res.success) throw new Error('Caricamento non riuscito');
        const types = calendarServiceTypeSchema.array().parse(res.data);
        if (!cancelled) { setServiceTypes(types); setTypesError(null); }
      } catch {
        if (!cancelled) setTypesError('Impossibile caricare i tipi di appuntamento.');
      }
    })();
    return () => { cancelled = true; };
  }, [canManage]);
  const sectionAllowed = visibleSections.some(s => s.id === activeSection);

  // Top-bar orizzontale (mobile/tablet). In embedded la mostro sempre in
  // cima al body perché non c'è l'header con titolo.
  const mobileNav = (
    <nav className={cn('lg:hidden', !embedded && 'border-t border-gray-200 dark:border-gray-800')}>
      <div
        className="flex overflow-x-auto overflow-y-hidden no-scrollbar snap-x snap-mandatory px-2"
        style={{ scrollbarWidth: 'none' }}
      >
        {visibleSections.map(s => (
          <button
            key={s.id}
            type="button"
            data-settings-tab={s.id}
            onClick={() => changeSection(s.id)}
            className={cn(
              'snap-start flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors',
              activeSection === s.id
                ? 'border-purple-500 text-purple-600 dark:text-purple-400 font-medium'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200',
            )}
          >
            {s.icon}
            <span>{s.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );

  return (
    <div className={cn(
      'flex flex-col h-full min-h-0 w-full min-w-0',
      embedded ? 'bg-transparent' : 'bg-gray-50 dark:bg-gray-950',
    )}>
      {/* Header sticky — solo in modalità pagina */}
      {!embedded && (
        <header className="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <div className="flex items-center justify-between gap-2 px-3 sm:px-6 py-3 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden flex-shrink-0"
                onClick={handleClose}
                aria-label="Torna al calendario"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <SettingsIcon className="w-5 h-5 text-purple-500 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                  Impostazioni Calendario
                </h1>
                <p className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 truncate">
                  Tutte le configurazioni del Calendar Hub
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              className="flex-shrink-0 hidden lg:inline-flex"
            >
              <X className="w-4 h-4 mr-1" />
              Chiudi
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="flex-shrink-0 lg:hidden"
              aria-label="Chiudi impostazioni"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          {mobileNav}
        </header>
      )}

      {/* In embedded la top-bar mobile/tablet è la prima cosa visibile */}
      {embedded && (
        <div className="lg:hidden flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          {mobileNav}
        </div>
      )}

      {/* Body: sidebar + content */}
      <div className="flex-1 min-h-0 flex w-full min-w-0">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex flex-col w-64 xl:w-72 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
          <nav className="p-3 space-y-1">
            {visibleSections.map(s => (
              <button
                key={s.id}
                type="button"
                data-settings-tab={s.id}
                onClick={() => changeSection(s.id)}
                className={cn(
                  'w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-md text-sm transition-colors min-w-0',
                  activeSection === s.id
                    ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                )}
              >
                <span className="flex-shrink-0 mt-0.5">{s.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{s.label}</span>
                  {s.description && (
                    <span className="block text-xs text-gray-500 dark:text-gray-500 truncate">
                      {s.description}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Content area */}
        <main
          ref={contentRef}
          className="flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden"
        >
          <div className="w-full min-w-0 max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
            {accessError && <p role="alert" className="text-sm text-destructive mb-4">{accessError}</p>}
            {!sectionAllowed && (
              <p role="alert" className="text-sm text-muted-foreground">
                {!access && !accessError ? 'Verifica dei permessi…' : 'Accesso negato: non hai i permessi richiesti per questa sezione.'}
              </p>
            )}
            {activeSection === 'general' && sectionAllowed && (
              <div className="space-y-6">
                <SettingsSection title="Limiti di prenotazione" icon={<Sliders className="w-5 h-5 text-purple-500" />}>
                  <CalendarBookingWindowSettings />
                </SettingsSection>

                <SettingsSection
                  title="Stati appuntamento"
                  description="Catalogo unico degli stati. Allineato a CRM, Kanban Appuntamenti diretti, automazioni e webhook."
                  icon={<CalendarIcon className="w-5 h-5 text-purple-500" />}
                >
                  <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/20 px-4 py-3 text-sm text-blue-900 dark:text-blue-200 mb-4">
                    <p className="font-semibold mb-1">Relazione con la campagna sistema "Appuntamenti diretti"</p>
                    <p className="leading-relaxed">
                      "Appuntamenti diretti" è una campagna CRM speciale che <strong>non usa pipeline stages</strong>:
                      le sue colonne Kanban corrispondono <strong>1:1</strong> a questi stati. Spostare una card tra colonne
                      aggiorna lo <code className="px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40">status</code> dell'appuntamento.
                       Le automazioni in <em>Impostazioni → Automazioni</em> filtrano sullo stesso elenco
                      tramite <code className="px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40">trigger_status</code>.
                    </p>
                  </div>

                  <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        <tr>
                          <th className="text-left px-4 py-2.5 font-medium">Stato</th>
                          <th className="text-left px-4 py-2.5 font-medium">Codice</th>
                          <th className="text-left px-4 py-2.5 font-medium">Descrizione e comportamento</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {APPOINTMENT_STATUS_OPTIONS.map((s) => (
                          <tr key={s.value} className="bg-white dark:bg-gray-900 hover:bg-gray-50/60 dark:hover:bg-gray-800/40">
                            <td className="px-4 py-3 align-top whitespace-nowrap">
                              <span className="inline-flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                                <span
                                  className="w-3 h-3 rounded-full ring-2 ring-white dark:ring-gray-900 shadow-sm"
                                  style={{ backgroundColor: s.color }}
                                />
                                {s.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 align-top whitespace-nowrap">
                              <code className="text-[11px] font-mono px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                                {s.value}
                              </code>
                            </td>
                            <td className="px-4 py-3 align-top text-gray-600 dark:text-gray-300 leading-relaxed">
                              {s.description}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-3">
                    Gli stati sono condivisi tra Calendario, CRM e automazioni e non sono modificabili da questa schermata.
                  </p>
                </SettingsSection>
              </div>
            )}

            {activeSection === 'types' && (
              <SettingsSection
                title="Tipi Appuntamento"
                 description="Configura durata, colore e servizi associati. Gli orari non dipendono dalla tipologia."
                icon={<CalendarIcon className="w-5 h-5 text-purple-500" />}
              >
                <ServiceTypesSettings />
              </SettingsSection>
            )}

            {activeSection === 'availability' && (
              <SettingsSection
                title="Disponibilità"
                description="Modalità di assegnazione e parametri di disponibilità"
                icon={<Clock className="w-5 h-5 text-purple-500" />}
              >
                <AvailabilityModeSettings />
              </SettingsSection>
            )}

            {activeSection === 'forms' && sectionAllowed && (
              <SettingsSection
                title="Form Pubblici"
                description="Gestione form di prenotazione pubblici"
                icon={<ExternalLink className="w-5 h-5 text-purple-500" />}
                contentClassName="p-0 sm:p-0 lg:p-0"
              >
                <div className="p-3 sm:p-4 lg:p-6 min-w-0 w-full">
                  <AppointmentFormsTab />
                </div>
              </SettingsSection>
            )}

            {activeSection === 'reassignments' && sectionAllowed && (
              <SettingsSection
                title="Riassegnazioni"
                description="Piani proposti da verificare e applicare manualmente"
                icon={<UserPlus className="w-5 h-5 text-purple-500" />}
              >
                <ReassignmentPlansPanel />
              </SettingsSection>
            )}

            {activeSection === 'automations' && sectionAllowed && (
              <SettingsSection
                title="Automazioni"
                description="Promemoria e automazioni post-appuntamento"
                icon={<Bell className="w-5 h-5 text-purple-500" />}
              >
                {typesError ? <p role="alert" className="text-sm text-destructive">{typesError}</p> : (
                  <AppointmentAutomations serviceTypes={serviceTypes.map(t => ({
                    id: t.id, name: t.name, color: t.color ?? '#6366f1', is_active: t.is_active ?? false,
                  }))} />
                )}
              </SettingsSection>
            )}

            {activeSection === 'notifications' && (
              <CalendarNotificationsTab />
            )}

            {activeSection === 'skill-overrides' && sectionAllowed && (
              <div className="space-y-4 sm:space-y-6 min-w-0 w-full">
                <SettingsSection
                  title="Audit override skill"
                  description="Cronologia delle override skill applicate dagli admin"
                  icon={<Lock className="w-5 h-5 text-purple-500" />}
                >
                  <SkillOverrideAuditPanel />
                </SettingsSection>
                <SettingsSection title="Audit override generale">
                  <OverrideAuditPanel />
                </SettingsSection>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
